import React from 'react';
import theme from './DependenciesEdit.module.scss';
import { CardHeader, SimpleCard } from '@/components/Cards';
import { Trans } from '@lingui/react/macro';
import { DependencyItemDto } from '@/services/dependencies';
import { ProjectCurrencyDisplay } from '@/seller/components/currency';
import { useGetDefaultProjectUnitOfMeasurement } from '@/utils/hooks';
import { convertToUnit, unitAbbreviations } from '@/utils/unit-of-measurements';
import InfoIconWithTooltip from '@/seller/components/InfoIconWithTooltip/InfoIconWithTooltip';
import { OptionItemDto, ProductSourceCatalog } from '@/types';
import { Checkbox, Badge } from '@ygtechnologies/rubus-ui';
import { classnames } from '@ygtechnologies/bygr-frontend-component-library';
import { CatalogProductThumbnails } from '@/seller/components/products/ProductThumbnails';
import { MeasurementDetailsField } from '@/seller/components/products/MeasurementDetailsField';

export interface DependencyBoxProps {
    option: OptionItemDto;
    hideCheckbox?: boolean;
    activeOptionId: number | null;
    onDependencyItemChange(v: DependencyItemDto): void;
    onSelect?(): void;
    checkboxPosition: 'left' | 'right';
    checked: boolean;
    onHover(): void;
    onLeave(): void;
    disabled?: boolean;
    onDisabledClick?(): void;
}

export const DependencyBox = ({
    option,
    hideCheckbox = false,
    activeOptionId,
    onDependencyItemChange,
    onSelect,
    checkboxPosition,
    checked,
    onHover,
    onLeave,
    disabled,
    onDisabledClick,
}: DependencyBoxProps) => {
    const unitOfMeasurement = useGetDefaultProjectUnitOfMeasurement()!;
    const unitAbbreviation = unitAbbreviations[unitOfMeasurement!];

    const checkbox = hideCheckbox ? (
        <div onClick={onSelect} className={classnames(theme.connector, theme.circle)} />
    ) : (
        <span onClick={disabled ? onDisabledClick : undefined} className={theme.connector}>
            <Checkbox
                disabled={disabled}
                className={theme.checkbox}
                inputSize="md"
                onChange={() => onDependencyItemChange({ from: activeOptionId!, to: option.id })}
                checked={checked}
            />
        </span>
    );
    const item = option.product;
    return (
        <div
            onMouseEnter={onHover}
            onMouseLeave={onLeave}
            className={classnames(theme.optionContainer, {
                [theme.active]: activeOptionId === option.id,
                [theme.hideCheckbox]: hideCheckbox,
                [theme.checkboxRight]: checkboxPosition === 'right',
                [theme.checkboxLeft]: checkboxPosition === 'left',
            })}>
            {checkboxPosition === 'left' && checkbox}
            <SimpleCard
                onClick={onSelect}
                className={classnames(theme.optionBox, {
                    [theme.active]: activeOptionId === option.id,
                    [theme.selectable]: !!onSelect,
                })}>
                {option.product.images[0] ? (
                    <CatalogProductThumbnails
                        catalog={ProductSourceCatalog.Project}
                        className={theme.productThumbnail}
                        disableOnClick
                        images={option.product.images}
                    />
                ) : (
                    <div className={theme.productThumbnail} />
                )}
                <div className={theme.headerWrapper}>
                    {option.isDefault && (
                        <Badge size="sm" variant="info">
                            <Trans>Default</Trans>
                        </Badge>
                    )}
                    <CardHeader>
                        <span className={theme.productName}>{option.product.name}</span>
                    </CardHeader>
                    <div className={theme.price}>
                        <ProjectCurrencyDisplay value={option.basePrice || 0} />
                    </div>
                </div>
                <InfoIconWithTooltip
                    placement="left"
                    content={
                        <div>
                            <div className={theme.details}>
                                <MeasurementDetailsField show={item.widthCm != null} caption={<Trans>Width</Trans>}>
                                    {convertToUnit(item.widthCm, unitOfMeasurement)}
                                    {unitAbbreviation}
                                </MeasurementDetailsField>

                                <MeasurementDetailsField show={item.heightCm != null} caption={<Trans>Height</Trans>}>
                                    {convertToUnit(item.heightCm, unitOfMeasurement)}
                                    {unitAbbreviation}
                                </MeasurementDetailsField>

                                <MeasurementDetailsField show={item.lengthCm != null} caption={<Trans>Length</Trans>}>
                                    {convertToUnit(item.lengthCm, unitOfMeasurement)}
                                    {unitAbbreviation}
                                </MeasurementDetailsField>

                                <MeasurementDetailsField show={item.depthCm != null} caption={<Trans>Depth</Trans>}>
                                    {convertToUnit(item.depthCm, unitOfMeasurement)}
                                    {unitAbbreviation}
                                </MeasurementDetailsField>
                            </div>
                        </div>
                    }
                />
            </SimpleCard>
            {checkboxPosition === 'right' && checkbox}
        </div>
    );
};
